import brand from '@helpers/brand';
import { cssVar, setCssVar } from '@helpers/cssVar';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { ReactComponent as SvgSearch } from '@img/icons/search.svg';
import LinkButtonStyles from '@stories/Components/Buttons/LinkButton/LinkButton.styles';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';
const Container = styled.header `
  ${setCssVar('accentColor', brand.greyVeryLight)};
  ${setCssVar('bgColor', brand.white)};
  ${setCssVar('fgColor', brand.black)};

  .theme-apl & {
    ${setCssVar('accentColor', brand.greyVeryLight)};
    ${setCssVar('bgColor', brand.white)};
    ${setCssVar('fgColor', brand.black)};
  }

  .theme-hh & {
    ${setCssVar('accentColor', brand.hortHouse.greyMid)};
    ${setCssVar('bgColor', brand.grey)};
    ${setCssVar('fgColor', brand.white)};
  }

  .theme-rss & {
    ${setCssVar('accentColor', brand.greyLight)};
    ${setCssVar('bgColor', brand.black)};
    ${setCssVar('fgColor', brand.white)};
  }

  color: ${cssVar('fgColor')};
  background-color: ${cssVar('bgColor')};
  position: relative;

  @media ${until(Device.TabletLarge)} {
    padding: 10px 0 11px;
  }
`;
const Grid = styled.div `
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 16px;

  @media ${from(Device.TabletLarge)} {
    grid-template-columns: repeat(24, 1fr);
    gap: 32px;
    align-items: center;
    height: 155px;
    position: relative;
  }

  ${({ hasTopLinks }) => hasTopLinks &&
    css `
      @media ${from(Device.TabletLarge)} {
        align-items: flex-end;
        padding-bottom: 30px;
      }
    `}
`;
const SearchArea = styled.div `
  grid-column: 1 / span 4;

  @media ${from(Device.TabletLarge)} {
    grid-column: 1 / span 10;
  }
`;
const ShowSearch = styled.button `
  background: transparent;
  border: none;

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const SearchIcon = styled(SvgSearch) `
  width: 30px;
  height: 30px;
  color: ${cssVar('fgColor')};
`;
const HomeLink = styled.a ``;
const LogoArea = styled.div `
  grid-column: 5 / span 4;
  text-align: center;

  @media ${from(Device.TabletLarge)} {
    grid-column: 11 / span 4;
  }
`;
const Logo = styled.img `
  width: 59px;
  height: 45px;

  @media ${from(Device.TabletLarge)} {
    width: 122px;
    height: 94px;
    justify-self: center;
  }

  .theme-apl & {
    width: 130px;
    height: 44px;

    @media ${from(Device.TabletLarge)} {
      width: 232px;
      height: 78px;
    }
  }

  .theme-rss & {
    width: 61px;
    height: 52px;

    @media ${from(Device.TabletLarge)} {
      width: 140px;
      height: 120px;
    }
  }
`;
const CtaArea = styled.div `
  grid-column: 9 / span 4;
  display: flex;
  justify-content: flex-end;

  @media ${from(Device.TabletLarge)} {
    grid-column: 15 / span 10;
  }
`;
const CtaInner = styled.div `
  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const CtaHolder = styled.div `
  display: flex;
  justify-content: space-between;

  ${LinkButtonStyles.Button} {
    margin-left: 20px;

    &:first-child {
      margin-left: 0;
    }
  }
`;
const TopLinkHolder = styled.div `
  position: absolute;
  top: 0;
  right: 0;
  width: auto;
  min-width: 344px;
  background-color: ${cssVar('accentColor')};
`;
const TopLinkList = styled.div `
  display: flex;
  justify-content: space-around;
  padding: 10px;
`;
const TopLinkItem = styled.a `
  ${fonts.sourceSansPro.regular};
  font-size: 16px;
  line-height: 22px;
  text-decoration: none;
  color: ${cssVar('fgColor')};
`;
const MobileBtnBar = styled.span `
  display: block;
  width: 28px;
  height: 2px;
  background-color: ${cssVar('fgColor')};
  position: absolute;
  top: 50%;
  transform-origin: center;

  &:first-child {
    transform: translateY(-50%);
    transition: transform 0.3s;
  }

  &:nth-child(2) {
    background-color: transparent;

    &:before {
      content: '';
      position: absolute;
      top: -10px;
      display: block;
      width: 28px;
      height: 2px;
      background-color: ${cssVar('fgColor')};
      transition: opacity 0.3s;
    }

    &:after {
      content: '';
      position: absolute;
      top: 8px;
      display: block;
      width: 28px;
      height: 2px;
      background-color: ${cssVar('fgColor')};
      transition: opacity 0.3s;
    }
  }

  &:last-child {
    transition: transform 0.3s;
    transform: translateY(-50%);
  }
`;
const MobileMenuBtn = styled.button `
  background-color: transparent;
  border: none;
  color: ${cssVar('fgColor')};
  width: 40px;
  position: relative;

  @media ${from(Device.TabletLarge)} {
    display: none;
  }

  ${({ openMenu }) => openMenu &&
    css `
      ${MobileBtnBar} {
        &:first-child {
          transform: translateY(-50%) rotate(-45deg);
        }

        &:nth-child(2) {
          &:before,
          &:after {
            opacity: 0;
          }
        }

        &:last-child {
          transform: translateY(-50%) rotate(45deg);
        }
      }
    `}
`;
const MobileNavHolder = styled(m.div) `
  position: relative;
  display: flex;
  background: #f6f6f6;
  height: 45px;

  @media ${until(Device.TabletLarge)} {
    position: fixed;
    top: 65px;
    left: 0;
    display: block;
    background: #fff;
    height: calc(100vh - 65px);
    overflow-y: auto;
    width: 100%;
    z-index: 8001;
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }

  .theme-rss & {
    @media ${until(Device.TabletLarge)} {
      top: 73px;
    }
  }
`;
export default {
    Container,
    CtaArea,
    CtaHolder,
    CtaInner,
    Grid,
    HomeLink,
    Logo,
    LogoArea,
    MobileBtnBar,
    MobileMenuBtn,
    MobileNavHolder,
    SearchArea,
    SearchIcon,
    ShowSearch,
    TopLinkHolder,
    TopLinkList,
    TopLinkItem,
};
